import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  FileText, CheckCircle2, Clock, AlertTriangle, Loader2, 
  CreditCard, Building2, Mail, Calendar, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/constants";
import axios from "axios";

const QuotePaymentPage = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API}/quotes/pay/${token}`);
        setQuote(response.data.quote);
        setExpired(response.data.expired);
      } catch (err) {
        console.error("Error fetching quote:", err);
        setError("Quote not found or has been removed.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchQuote();
    }
  }, [token]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/quotes/pay/${token}/checkout`);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Unable to start checkout. Please try again or contact support.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center" data-testid="quote-payment-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00E676] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-20" data-testid="quote-payment-error">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Quote Not Found</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <Link to="/contact">
            <Button className="btn-ninja">Contact Support</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (quote?.status === "paid") {
    return (
      <div className="min-h-screen pt-32 pb-20" data-testid="quote-payment-paid">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#00E676]" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Payment Complete</h1>
          <p className="text-gray-400 mb-8">
            This quote has already been paid. Our team will be in touch shortly to begin your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="btn-ninja">Return Home</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-outline">Contact Us</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen pt-32 pb-20" data-testid="quote-payment-expired">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Quote Expired</h1>
          <p className="text-gray-400 mb-8">
            This quote has expired. Please contact us to request a new quote.
          </p>
          <Link to="/contact">
            <Button className="btn-ninja">Request New Quote</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="quote-payment-page">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-[#00E676]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Quote</h1>
          <p className="text-gray-400">Quote #{quote?.id}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Quote Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Service Card */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#00E676]" />
                Service Details
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Service</p>
                  <p className="text-white font-medium">{quote?.service_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="text-gray-300">{quote?.description}</p>
                </div>
                {quote?.line_items && quote.line_items.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-gray-500 mb-2">Included:</p>
                    <ul className="space-y-2">
                      {quote.line_items.map((item, idx) => (
                        <li key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-300">{item.name}</span>
                          {item.price && (
                            <span className="text-white">{formatCurrency(item.price, quote.currency)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00E676]" />
                Prepared For
              </h2>
              <div className="space-y-2">
                <p className="text-white font-medium">{quote?.customer_name}</p>
                {quote?.company && <p className="text-gray-400">{quote.company}</p>}
                <p className="text-gray-400">{quote?.customer_email}</p>
              </div>
            </div>

            {/* Notes */}
            {quote?.notes && (
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Notes</h2>
                <p className="text-gray-300 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}
          </div>

          {/* Payment Summary */}
          <div className="md:col-span-1">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 sticky top-24">
              <h2 className="text-lg font-semibold mb-6">Payment Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-white">{formatCurrency(quote?.amount, quote?.currency)}</span>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-2xl font-bold text-[#00E676]">
                      {formatCurrency(quote?.amount, quote?.currency)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
                <Calendar className="w-4 h-4" />
                <span>Valid until {formatDate(quote?.valid_until)}</span>
              </div>

              <Button 
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="btn-ninja w-full text-base py-6"
                data-testid="pay-quote-btn"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay Now
                  </>
                )}
              </Button>

              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Secure payment powered by Stripe
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm text-gray-400 mb-2">Questions about this quote?</p>
                <Link to="/contact" className="text-[#00E676] text-sm hover:underline flex items-center gap-1">
                  Contact our team <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotePaymentPage;
